import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import OvImageLocalUploadDialog from './OvImageLocalUploadDialog';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import ImageIcon from '@mui/icons-material/Image';
import OvLinkButton from '../atoms/OvLinkButton';
import { IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ReactDOM from 'react-dom';
import OvImageCropDialog from './OvImageCropDialog';

interface OvImageLocalCropUploadProps {
  imageBasePath: string;
  imageUrl?: string;
  title?: string;
  ctaLabel?: string;
  hintText?: string;
  dialogTitle: string;
  handleChange: (updatedProperties: any) => void;
  resizeOptions?: {
    resizedImagePath: string;
    maxWidth: number;
    maxHeight: number;
    quailty: number;
  };
}

const OvImageLocalCropUpload: FC<OvImageLocalCropUploadProps> = (props) => {
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [isCropDialogOpen, setIsCropDialogOpen] = useState<boolean>(false);

  // The final remote URL or the existing image
  const [imagePath, setImagePath] = useState<string>('');

  // We'll store the local blob URL after picking the file
  const [localBlobUrl, setLocalBlobUrl] = useState<string>('');

  const previewImageStyles: React.CSSProperties = {
    width: '100%',
    height: 'auto',
    objectFit: 'contain',
    borderRadius: '1.25rem',
    maxWidth: '18rem',
  };

  // If there's a preexisting "final" URL, set it
  useEffect(() => {
    if (props.imageUrl) {
      setImagePath(props.imageUrl);
    }
  }, [props.imageUrl]);

  const onLocalFileSelected = ({
    localFile,
    localUrl,
  }: {
    localFile: File;
    localUrl: string;
  }) => {
    setLocalBlobUrl(localUrl);
    setIsDialogOpen(false);
    setIsCropDialogOpen(true);
  };

  const onSuccessfulCrop = (croppedImagePath: string) => {
    // Store that final remote URL
    setImagePath(croppedImagePath);

    // Let parent know we have a new remote image + "is_cropped_image" = true
    props.handleChange({
      image_path: croppedImagePath,
      is_cropped_image: true,
    });

    setIsCropDialogOpen(false);
  };

  // If the user removes the existing image
  const onRemoveUploadedImage = () => {
    setImagePath('');
    props.handleChange('');
  };

  return (
    <>
      <Title>{props.title}</Title>

      {imagePath ? (
        <ImageWrapper>
          <RemoveImageIconButton onClick={onRemoveUploadedImage}>
            <DeleteIcon />
          </RemoveImageIconButton>

          <img src={imagePath} alt={props.title} style={previewImageStyles} />
        </ImageWrapper>
      ) : (
        // If no final image, show an "upload" box
        <Wrapper>
          <StyledImage />
          <UploadSection>
            <OvLinkButton onClick={() => setIsDialogOpen(true)}>
              {props.ctaLabel || 'Upload Image'}
            </OvLinkButton>
            <HintText>{props.hintText}</HintText>
          </UploadSection>
        </Wrapper>
      )}

      {isDialogOpen &&
        ReactDOM.createPortal(
          <OvImageLocalUploadDialog
            isOpen={isDialogOpen}
            onCancel={() => setIsDialogOpen(false)}
            title={props.dialogTitle}
            onLocalFileSelected={onLocalFileSelected}
          />,
          document.body
        )}

      {isCropDialogOpen &&
        ReactDOM.createPortal(
          <OvImageCropDialog
            isOpen={isCropDialogOpen}
            onCancel={() => setIsCropDialogOpen(false)}
            originalImagePath={localBlobUrl}
            imageBasePath={props.imageBasePath}
            onCropSuccess={onSuccessfulCrop}
          />,
          document.body
        )}
    </>
  );
};

export default OvImageLocalCropUpload;

const Title = styled.h4`
  font-size: ${Variables.fontSizes.MEDIUM};
  text-indent: 0.5rem;
  margin-bottom: 0.5rem;
`;

const Wrapper = styled.div`
  display: flex;
  gap: 1rem;
  flex-direction: row;
  border: 2px dashed rgba(78, 92, 101, 0.46);
  padding: 1.25rem;
  border-radius: ${Variables.borderRadius.MEDIUM};
`;

const StyledImage = styled(ImageIcon)`
  && {
    color: ${Colours.BLUE['300']};
    width: 2.5rem;
    height: 2.5rem;
  }
`;

const UploadSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.125rem;
`;

const HintText = styled.p`
  margin: 0;
  color: ${Colours.TEXT_DARK['500']};
  font-size: ${Variables.fontSizes.MEDIUM};
`;

const ImageWrapper = styled.div`
  max-width: 8.5rem;
  border-radius: 0.75rem;
  object-fit: cover;
  position: relative;
`;

const RemoveImageIconButton = styled(IconButton)`
  && {
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
    background-color: white;
    width: 1.5rem;
    height: 1.5rem;
    color: ${Colours.BLUE['500']};
    font-size: 1.5rem;
    position: absolute;
    top: 0.25rem;
    right: 0.25rem;
    cursor: pointer;
    padding: 0;

    &:hover {
      background-color: white;
      color: ${Colours.BLUE['700']};
    }

    .MuiSvgIcon-root {
      width: 1.25rem;
      height: 1.25rem;
    }
  }
`;
